import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Injector,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { MatSlideToggle, MatSlideToggleChange } from '@angular/material/slide-toggle';
import { IInputComponent } from '@nimbus-library/features/shared/components/material/i-input-component';

@Component({
  standalone: false,
  selector: 'nimbus-input-slide-toggle',
  templateUrl: './input-slide-toggle.component.html',
  styleUrls: ['./input-slide-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputSlideToggleComponent),
    },
  ],
})
export class InputSlideToggleComponent extends IInputComponent<boolean> implements AfterViewInit, OnDestroy {
  @HostBinding('class')
  public class: string = 'nimbus-input-slide-toggle';

  @Input()
  public stopPropagation: boolean = false;
  @Input()
  public isInverted: boolean = false;
  @Input()
  public isSmall: boolean = false;
  @Input()
  public isBeta: boolean;
  @Input()
  public ariaLabel: string;
  @Input()
  public warningTooltipPanelClass: string = '';

  @Output()
  public changeValue: EventEmitter<MatSlideToggleChange> = new EventEmitter<MatSlideToggleChange>();

  @ViewChild(MatSlideToggle)
  public matSlideToggle: MatSlideToggle;

  private clickListener: ((event: MouseEvent) => void) | null = null;
  private keydownListener: ((event: KeyboardEvent) => void) | null = null;
  private buttonEl: HTMLButtonElement | null = null;

  public constructor(
    public changeDetectionRef: ChangeDetectorRef,
    injector: Injector,
    translateService: TranslateService,
  ) {
    super(changeDetectionRef, injector, translateService);
  }

  public ngAfterViewInit(): void {
    if (this.stopPropagation) {
      this.buttonEl = this.matSlideToggle['_elementRef']?.nativeElement?.querySelector('button');
      if (this.buttonEl) {
        this.clickListener = (event: MouseEvent) => {
          event.stopPropagation();
        };
        this.keydownListener = (event: KeyboardEvent) => {
          if (event.key === ' ' || event.key === 'Spacebar' || event.key === 'Enter') {
            event.stopPropagation();
          }
        };
        this.buttonEl.addEventListener('click', this.clickListener);
        this.buttonEl.addEventListener('keydown', this.keydownListener);
      }
    }
  }

  public ngOnDestroy(): void {
    if (this.buttonEl) {
      if (this.clickListener) {
        this.buttonEl.removeEventListener('click', this.clickListener);
      }
      if (this.keydownListener) {
        this.buttonEl.removeEventListener('keydown', this.keydownListener);
      }
    }
  }

  public onChange(event: MatSlideToggleChange): void {
    this.changeValue.emit(event);
    this.changeDetectionRef.markForCheck();
  }

  public onSuffixClick(): void {
    if (this.isDisabled) {
      return;
    }
    this.matSlideToggle.toggle();
    this.changeValue.emit(new MatSlideToggleChange(this.matSlideToggle, this.matSlideToggle.checked));
  }
}
